/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { PriceComponent } from '@msdyn365-commerce/components';
import { CheckoutModule, ErrorLocation, ICheckoutState } from '@msdyn365-commerce/global-state';
import { Cart, ChannelDeliveryOptionConfiguration, FilterDeliveryModeOption } from '@msdyn365-commerce/retail-proxy';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import {
    Address,
    CartLine,
    DeliveryOption,
    DeliveryPreferenceType,
    LineDeliverySpecification,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import {
    GetDeliveryOptionsForCartLineAddressInput,
    getDeliveryOptionsForCartLineAddress,
    getFallbackImageUrl,
    QueryResultSettingsProxy,
    StringExtensions,
    DeliveryOptionsCartLineAddress,
    ArrayExtensions,
    ObjectExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, Waiting } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import get from 'lodash/get';
import { action, computed, observable, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import groupBy from 'lodash/groupBy';

import { IImageProps, IImageSettings } from '@msdyn365-commerce/core';
import { ErrorComponent } from '../../common/error';
import { ICheckoutDeliveryOptionsData } from './checkout-delivery-options.data';
import {
    filterDeliveryModeOption,
    ICheckoutDeliveryOptionsProps,
    useDefaultDeliveryOption
} from './checkout-delivery-options.props.autogenerated';
import DescriptionComponent from './components/checkout-delivery-options.description';
import ErrorMessageComponent from './components/checkout-delivery-options.error-message';
import ErrorTitleComponent from './components/checkout-delivery-options.error-title';
import RadioButtonComponent from './components/checkout-delivery-options.radio-button';
import { focusOnCheckoutError } from '../../checkout-errors';
import { AddressDetailItemFunctionComponent } from './components/address-detail-item';
import CheckoutCartLineDeliveryDate from './components/checkout-cartLine-delivery-date';
import CheckoutCartLineDeliveryOptionsRadio from './components/checkout-cartLine-delivery-options-radio';
import { GetLineItems } from './components/get-line-items';
import {
    CheckoutShippingCartLines,
    ICheckoutShippingCartLineInfo,
    ICheckoutShippingCartLinesProps,
    defaultImageSettings
} from './components/checkout-shipping-cartlines-images';

export interface ICheckoutDeliveryOptionsModuleProps
    extends ICheckoutDeliveryOptionsProps<ICheckoutDeliveryOptionsData>,
        IModuleStateProps {}

export interface ICheckoutDeliveryOption {
    DeliveryOption: INodeProps;
    description?: React.ReactNode;
    price?: React.ReactNode;
}

export interface ICartLineSelectedDeliveryOption {
    cartLineId: string;
    selectedDeliveryOption: string;
    requestDeliveryDate?: string;
}

export interface ICheckoutDeliveryOptionEdit extends ICheckoutDeliveryOption {
    code: string;
    radioButton?: React.ReactNode;
}

export interface ICheckoutDeliveryOptionsList {
    DeliveryOptionsList: INodeProps;
    list: ICheckoutDeliveryOptionEdit[];
}

export interface ICheckoutDeliveryOptionsError {
    Error: INodeProps;
    title: React.ReactNode;
    message: React.ReactNode;
}

export interface ICheckoutDeliveryOptionsViewState {
    isLoading: boolean;
    isError: boolean;
    isShowList: boolean;
    isShowSelected: boolean;
    isShowSaveButton: boolean;
    isShowEditButton: boolean;
    isShowCancelButton: boolean;
}

export interface ICheckoutDeliveryOptionsViewProps extends ICheckoutDeliveryOptionsProps<{}> {
    CheckoutDeliveryOptions: IModuleProps;
    selectedItemId: string;
    deliveryOptionsData: DeliveryOption[];
    isLoading: boolean;
    errorMessageData: string;
    viewState: ICheckoutDeliveryOptionsViewState;
    deliveryOptions?: ICheckoutDeliveryOptionsList;
    deliveryOptionSelected?: ICheckoutDeliveryOption;
    errorMessage?: ICheckoutDeliveryOptionsError;
    waiting?: React.ReactNode;
    saveButton?: React.ReactNode;
    editButton?: React.ReactNode;
    cancelButton?: React.ReactNode;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;
    checkoutCartLineGroupDeliveryOptions?: React.ReactNode;
    enableCheckoutCartLineDeliveryOptions?: boolean;
    cartLineImages?: React.ReactNode;
    onDeliveryOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onSave(): void;
    onEdit(): void;
    onCancel(): void;
}

/**
 *
 * CheckoutDeliveryOptions component.
 * @extends {React.Component<ICheckoutDeliveryOptionsProps<ICheckoutDeliveryOptionsData>>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutDeliveryOptions extends React.Component<ICheckoutDeliveryOptionsModuleProps> {
    @observable private selectedItemId: string = '';

    @observable private deliveryOptions: DeliveryOption[] = [];

    @observable private isLoading: boolean = false;

    @observable private errorMessage?: string;

    @observable private groupDeliveryOptions: DeliveryOptionsCartLineAddress[] = [];

    @observable private cartLineSelectedDeliveryOptions: ICartLineSelectedDeliveryOption[] = [];

    @computed private get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    @computed get currencyCode(): string | undefined {
        return get(this.props, 'context.request.channel.Currency');
    }

    public get expressPaymentDetailsFromCartPage() {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
        const properties =
            this.props.data.cart?.result?.cart?.ExtensionProperties?.find(property => property.Key === 'expressPaymentDetails')?.Value
                ?.StringValue ?? '';

        return properties ? JSON.parse(properties) : null;
    }

    public get isExpressPaymentAppliedInCartPage(): boolean {
        return this.expressPaymentDetailsFromCartPage?.isExpressCheckoutAppliedInCartPage ?? false;
    }

    private cartDeliveryMode?: string;

    private retailMultiplePickUpOptionEnabled?: boolean = false;

    private channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    private readonly telemetryContent?: ITelemetryContent;

    private readonly isSelectDefaultOptionEnabled?: boolean;

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    private readonly isMultipleAddressShippingEnabled?: boolean;

    private readonly timeoutRetailCall: number;

    private cartLinesDeliveryOptions: ICartLineSelectedDeliveryOption[] = [];

    public constructor(props: ICheckoutDeliveryOptionsModuleProps) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.isSelectDefaultOptionEnabled =
            this.props.config.useDefaultDeliveryOption && this.props.config.useDefaultDeliveryOption !== useDefaultDeliveryOption.none;
        this.isMultipleAddressShippingEnabled = this.props.config.multipleAddressShippingEnabled;
        this.timeoutRetailCall = this.props.config.timeoutToCallRetail !== undefined ? this.props.config.timeoutToCallRetail : 0;
    }

    public async componentDidMount(): Promise<void> {
        when(
            () => this.isDataReady,
            async () => {
                if (
                    (this.props.data.checkout.result?.isExpressCheckoutApplied && this.canShip()) ||
                    this.isExpressPaymentAppliedInCartPage
                ) {
                    await this.init();
                } else {
                    // Wait for a specific time (e.g., 2000 milliseconds = 2 seconds)
                    await new Promise(resolve => setTimeout(resolve, this.timeoutRetailCall));
                    // Then call the init function
                    await this.init();
                }
            }
        );

        reaction(
            () => this.props.data.checkout.result?.shippingAddress,
            async () => {
                await this.loadDeliveryOptions();
                this.props.moduleState.setIsCancellable(false);

                await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
            }
        );

        reaction(
            () => this.props.data.checkout.result?.cartLinesWithShippingAddress,
            async () => {
                await this.loadDeliveryOptions();
                this.props.moduleState.setIsCancellable(false);

                await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                if (isExpressCheckoutApplied && this.canShip() && !this.isExpressPaymentAppliedInCartPage) {
                    await this.loadDeliveryOptions();
                    this.props.moduleState.setIsCancellable(false);

                    await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
                }
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (
                        checkoutError &&
                        checkoutError.errorLocation === ErrorLocation.CheckoutDeliveryOptions &&
                        checkoutError.errorMessage
                    ) {
                        this.setError(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutDeliveryOptions) {
                        focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutDeliveryOptionsModuleProps, nextState: ICheckoutDeliveryOptionsViewState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        this.channelDeliveryOptionConfig = this.props.data.channelDeliveryOptionConfig?.result;
        this.retailMultiplePickUpOptionEnabled = this.props.data.featureState?.result?.find(
            feature => feature.Name === multiplePickupStoreSwitchName
        )?.IsEnabled;

        if (!this.canShip()) {
            this.props.context.telemetry.error('Checkout delivery options content is empty, module wont render');
            return null;
        }

        const {
            moduleState: { isReady, isUpdating, hasError }
        } = this.props;
        const isError = !this.isLoading && hasError;
        const isShowList = !this.isLoading && !hasError && isUpdating && !this.isSelectDefaultOptionEnabled;
        const isShowSelected = !this.isLoading && !hasError && isReady;
        const { errorMessageTitle, deliveryOptionAriaLabel } = this.props.resources;
        const cartLinesDisplay = this.getMultipleDeliveryOptionSelected();
        const lineImageProps: ICheckoutShippingCartLinesProps = {
            moduleClassName: 'ms-checkout-shipping-address',
            cartLines: cartLinesDisplay.filter(x => !ObjectExtensions.isNullOrUndefined(x))
        };

        const cartLineImages: React.ReactNode = <CheckoutShippingCartLines {...lineImageProps} />;

        const viewProps = {
            ...this.props,
            onDeliveryOptionChange: this.onDeliveryOptionChange,
            selectedItemId: this.selectedItemId,
            deliveryOptionsData: this.deliveryOptions,
            isLoading: this.isLoading,
            alert: this.errorMessage && <ErrorComponent {...{ title: errorMessageTitle, message: this.errorMessage }} />,
            errorMessageData: this.errorMessage,
            viewState: {
                isLoading: this.isLoading,
                isError,
                isShowList,
                isShowSelected,
                isShowSaveButton: isShowList && !this.props.moduleState.hasExternalSubmitGroup,
                isShowEditButton: isShowSelected && !this.props.moduleState.hasExternalSubmitGroup,
                isShowCancelButton: isShowList && !this.props.moduleState.hasExternalSubmitGroup && !!this.getSavedDeliveryOption()
            },
            CheckoutDeliveryOptions: {
                moduleProps: this.props,
                className: classname('ms-checkout-delivery-options', this.props.config.className)
            },
            deliveryOptions: {
                DeliveryOptionsList: {
                    className: 'ms-checkout-delivery-options__list',
                    role: 'list',
                    'aria-label': deliveryOptionAriaLabel
                },
                list: this.getDeliveryOptionList()
            },
            deliveryOptionSelected: this.getDeliveryOptionSelected(),
            errorMessage: this.getErrorMessage(),
            waiting: <Waiting />,
            checkoutErrorRef: this.checkoutErrorRef,
            checkoutCartLineGroupDeliveryOptions: this.getCheckoutCartLineGroupDeliveryOptions(),
            enableCheckoutCartLineDeliveryOptions: this.isMultipleAddressShippingEnabled,
            cartLineImages,
            saveButton: this.getSaveButton(),
            editButton: this.getEditButton(),
            cancelButton: this.getCancelButton(),
            onSave: this.onSave,
            onEdit: this.onEdit,
            onCancel: this.onCancel
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    @action
    private readonly init = async (): Promise<void> => {
        this.props.moduleState.init({
            onEdit: this.isSelectDefaultOptionEnabled ? undefined : this.onEdit,
            onCancel: this.isSelectDefaultOptionEnabled ? undefined : this.onCancel,
            onSubmit: this.isSelectDefaultOptionEnabled ? undefined : this.onSubmit,
            status:
                !(this.props.config.useDefaultDeliveryOption === useDefaultDeliveryOption.notDisplaySelectedDeliveryOption) &&
                this.canShip()
                    ? 'updating'
                    : 'disabled'
        });

        this.cartDeliveryMode = this.getSavedDeliveryOption();
        if (this.isMultipleAddressShippingEnabled) {
            this.getCartLinesSavedDeliveryOption();
        }
        await this.loadDeliveryOptions();
    };

    private readonly canShip = (): boolean => {
        const hasInvoice = this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;
        return !hasInvoice && this.getCartLinesForShipping().length > 0;
    };

    private readonly isDeliveryModeExists = (deliveryMode: string | undefined): boolean => {
        if (deliveryMode) {
            if (this.deliveryOptions.find(deliveryOption => deliveryOption.Code === deliveryMode)) {
                return true;
            }
        }
        return false;
    };

    private readonly getDeliveryOptionSelected = (): ICheckoutDeliveryOption | null => {
        const selectedItem = this.deliveryOptions.find(deliveryOption => deliveryOption.Code === this.selectedItemId);
        if (!selectedItem) {
            this.props.context.telemetry.error('Checkout delivery options content is empty, module wont render');
            return null;
        }

        const { id, typeName, context } = this.props;
        const { priceFree } = this.props.resources;
        const price = get(selectedItem, 'ShippingChargeAmount', 0);
        const channelConfig = this.props.context.request.channel;
        return {
            DeliveryOption: { className: 'ms-checkout-delivery-options__option-selected' },
            description: <DescriptionComponent {...{ description: selectedItem.Description }} />,
            price: (
                <PriceComponent
                    id={id}
                    typeName={typeName}
                    context={context}
                    className='ms-checkout-delivery-options__price'
                    data={{ price: { CustomerContextualPrice: price } }}
                    freePriceText={priceFree}
                    currencyCode={channelConfig?.Currency}
                />
            )
        };
    };

    private readonly getErrorMessage = (): ICheckoutDeliveryOptionsError => {
        const { errorMessageTitle } = this.props.resources;
        return {
            // @ts-expect-error
            CheckoutDeliveryOptionsError: { tag: 'span', className: 'ms-address-form__error', role: 'alert', 'aria-live': 'assertive' },
            title: <ErrorTitleComponent {...{ title: errorMessageTitle }} />,
            message: <ErrorMessageComponent {...{ message: this.errorMessage }} />
        };
    };

    private readonly getDeliveryOptionList = (): ICheckoutDeliveryOptionEdit[] => {
        const count = this.deliveryOptions.length;
        const { priceFree } = this.props.resources;
        const { id, typeName, context } = this.props;
        const channelConfig = this.props.context.request.channel;

        return this.deliveryOptions.map((deliveryOption: DeliveryOption, index: number) => {
            const getAriaLabelWithPrice = get(deliveryOption, 'ShippingChargeAmount', 0);
            return {
                DeliveryOption: { className: 'ms-checkout-delivery-options__option', role: 'listitem' },
                code: deliveryOption.Code || '',
                radioButton: (
                    <RadioButtonComponent
                        {...{
                            isChecked: deliveryOption.Code === this.selectedItemId,
                            value: deliveryOption.Code,
                            ariaSetSize: count,
                            ariaPosInSet: index + 1,
                            ariaLabel: `${deliveryOption.Description!} ${getAriaLabelWithPrice === 0 ? priceFree : getAriaLabelWithPrice}`,
                            onChange: this.onDeliveryOptionChange,
                            telemetryContent: this.telemetryContent
                        }}
                    />
                ),
                description: (
                    <DescriptionComponent
                        {...{
                            description: deliveryOption.Description
                        }}
                    />
                ),
                price: (
                    <PriceComponent
                        id={id}
                        typeName={typeName}
                        context={context}
                        className='ms-checkout-delivery-options__price'
                        data={{ price: { CustomerContextualPrice: get(deliveryOption, 'ShippingChargeAmount', 0) } }}
                        freePriceText={priceFree}
                        currencyCode={channelConfig?.Currency}
                    />
                )
            };
        });
    };

    private readonly getSaveButton = (): React.ReactNode => {
        const { saveBtnLabel = 'Save' } = this.props.resources;
        return (
            <Button
                className='ms-checkout-delivery-options__btn-save'
                title={saveBtnLabel}
                color='primary'
                disabled={!this.selectedItemId}
                onClick={this.onSave}
            >
                {saveBtnLabel}
            </Button>
        );
    };

    private readonly getEditButton = (): React.ReactNode => {
        const { editBtnLabel = 'Change' } = this.props.resources;
        return (
            <Button className='ms-checkout-delivery-options__btn-edit' title={editBtnLabel} color='primary' onClick={this.onEdit}>
                {editBtnLabel}
            </Button>
        );
    };

    private readonly getCancelButton = (): React.ReactNode => {
        const { cancelBtnLabel = 'Cancel' } = this.props.resources;
        return (
            <Button className='ms-checkout-delivery-options__btn-cancel' title={cancelBtnLabel} color='secondary' onClick={this.onCancel}>
                {cancelBtnLabel}
            </Button>
        );
    };

    private readonly setError = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.errorMessage = errorMessage;
    };

    private readonly clearError = (): void => {
        this.props.moduleState.setHasError(false);
        this.errorMessage = undefined;
    };

    private readonly onSave = async (): Promise<void> => {
        await this.onSubmit();
    };

    private readonly onEdit = async (): Promise<void> => {
        this.props.telemetry.information('Delivery options onEdit is called.');

        this.clearError();
        this.onUpdating();
        await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Delivery options onSubmit is called.');
        if (this.isMultipleAddressShippingEnabled) {
            this.updateCartLinesDeliverySpecification();
        } else {
            await this.updateCartDeliverySpecification(this.selectedItemId);
        }
        await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });
    };

    private readonly onCancel = async (): Promise<void> => {
        this.props.telemetry.information('Delivery options onCancel is called.');

        // Reset to the saved value
        const savedDeliveryOption = this.getSavedDeliveryOption();
        if (savedDeliveryOption) {
            this.selectedItemId = savedDeliveryOption;
            this.onReady();

            await this.props.data.checkout.result?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });
        } else {
            this.onUpdating();
        }
    };

    private readonly getSavedDeliveryOption = (): string | undefined => {
        const cartLinesForShipping = this.getCartLinesForShipping();
        return cartLinesForShipping[0] && cartLinesForShipping[0].DeliveryMode;
    };

    private readonly getCartLinesSavedDeliveryOption = (): void => {
        const cartLinesForShipping = this.getCartLinesForShipping();
        cartLinesForShipping.forEach(cartLine => {
            const deliveryMode = cartLine.DeliveryMode !== undefined ? cartLine.DeliveryMode : '';
            const requestDeliveryDate = cartLine.RequestedDeliveryDate !== undefined ? cartLine.RequestedDeliveryDate.toISOString() : '';
            if (!cartLine.LineId || StringExtensions.isNullOrEmpty(deliveryMode)) {
                return;
            }
            const cartLineId = cartLine?.LineId;
            const existingCartLineSelectedDeliveryOption = this.cartLinesDeliveryOptions.find(
                cartLineDeliveryOption => cartLineDeliveryOption.cartLineId === cartLineId
            );
            if (existingCartLineSelectedDeliveryOption) {
                existingCartLineSelectedDeliveryOption.selectedDeliveryOption = deliveryMode;
                existingCartLineSelectedDeliveryOption.requestDeliveryDate = requestDeliveryDate;
            } else {
                if (cartLineId !== undefined) {
                    this.cartLinesDeliveryOptions.push({
                        cartLineId,
                        selectedDeliveryOption: deliveryMode,
                        requestDeliveryDate: requestDeliveryDate
                    });
                }
            }
        });
    };

    private readonly updateCartDeliverySpecification = async (deliveryModeId: string): Promise<void> => {
        if (deliveryModeId.trim() === '') {
            return;
        }

        const { resources } = this.props;
        const checkoutState = this.props.data.checkout.result as ICheckoutState;
        const updateCartDeliverySpecificationInput = {
            deliveryModeId,
            shippingAddress: checkoutState.shippingAddress,
            channelDeliveryOptionConfig: this.retailMultiplePickUpOptionEnabled ? this.channelDeliveryOptionConfig : undefined
        };

        this.clearError();
        this.onPending();

        await checkoutState.checkoutCart
            .updateCartDeliverySpecification(updateCartDeliverySpecificationInput)
            .then(response => {
                if (response.status === 'FAILED') {
                    const errorMessage = `${response.errorDetails?.LocalizedMessage}`;
                    this.setError(StringExtensions.isNullOrEmpty(errorMessage) ? resources.genericErrorMessage : errorMessage);
                }

                this.onReady();
                this.props.moduleState.setIsCancellable(true);
            })
            .catch(() => {
                this.setError(resources.genericErrorMessage);
            });
    };

    @action
    private readonly updateCartLinesDeliverySpecification = async (): Promise<void> => {
        if (!this.cartLineSelectedDeliveryOptions || !this.cartLineSelectedDeliveryOptions.length) {
            return;
        }
        const { resources } = this.props;
        let lineSpecs: LineDeliverySpecification[] = [];
        const checkoutCartLines = this.props.data.checkout.result?.checkoutCart.cart.CartLines;

        this.cartLineSelectedDeliveryOptions.forEach(group => {
            const address = checkoutCartLines?.find(cartLine => cartLine.LineId === group.cartLineId)?.ShippingAddress;
            const lineSpec = {
                LineId: group.cartLineId,
                DeliverySpecification: {
                    DeliveryModeId: group.selectedDeliveryOption,
                    DeliveryPreferenceTypeValue: DeliveryPreferenceType.ShipToAddress,
                    DeliveryAddress: address,
                    RequestedDeliveryDate: group.requestDeliveryDate ? new Date(group.requestDeliveryDate) : undefined
                }
            };
            lineSpecs.push(lineSpec);
        });

        try {
            const checkoutState = this.props.data.checkout.result as ICheckoutState;
            const response = await checkoutState.checkoutCart.updateCartLinesDeliverySpecification({
                lineDeliverySpecifications: lineSpecs
            });

            if (response.status === 'FAILED') {
                this.setError(resources.genericErrorMessage);
            }
            this.onReady();
            this.props.moduleState.setIsCancellable(true);
        } catch {
            this.setError(resources.genericErrorMessage);
        }
    };

    private readonly getCartLinesForShipping = (): CartLine[] => {
        const { request } = this.props.context;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;

        return this.retailMultiplePickUpOptionEnabled
            ? ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !==
                            this.channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(
                                deliveryMode => deliveryMode === cartLine.DeliveryMode
                            ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              )
            : ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).filter(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              );
    };

    private readonly getDeliveryOptions = async (shippingAddress: Address): Promise<void> => {
        if (!this.props.data.checkout.result) {
            return;
        }

        const { context, resources, config } = this.props;
        const cartState = this.props.data.checkout.result?.checkoutCart;
        const cartId = (cartState.cart as Cart).Id;

        if (context) {
            this.isLoading = true;
            this.deliveryOptions = [];
            this.selectedItemId = '';
            this.clearError();

            try {
                let originalDeliveryMode: string = '';
                const shippingCartLines = this.getCartLinesForShipping();
                const hasShippingDeliveryLines = shippingCartLines && shippingCartLines.length > 0 && shippingCartLines[0].DeliveryMode;
                if (hasShippingDeliveryLines) {
                    // First store the delivery mode, so that we can restore the shipping information.
                    originalDeliveryMode = shippingCartLines[0].DeliveryMode || '';

                    // Clear the delivery information on retail server, so GetDeliveryOptions charge calculation works correctly
                    const cart = cartState.cart;
                    await cartState.clearCartLinesDeliveryInformation({
                        cart,
                        channelDeliveryOptionConfiguration: this.channelDeliveryOptionConfig
                    });
                }

                // TODO: Check if we can move this getDeliveryOptions to checkout state
                let deliveryOptionFilter: FilterDeliveryModeOption;
                switch (config.filterDeliveryModeOption) {
                    case filterDeliveryModeOption.none:
                        deliveryOptionFilter = FilterDeliveryModeOption.None;
                        break;
                    case filterDeliveryModeOption.filterOutNonCarrierDeliveryMode:
                        deliveryOptionFilter = FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode;
                        break;
                    default:
                        deliveryOptionFilter = FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode;
                }
                const response = await getDeliveryOptionsAsync(
                    {
                        callerContext: context.actionContext,
                        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context.actionContext)
                    },
                    cartId,
                    shippingAddress,
                    [],
                    deliveryOptionFilter,
                    true
                );

                if (hasShippingDeliveryLines) {
                    await this.updateCartDeliverySpecification(originalDeliveryMode);
                }

                if (response && response.length > 0) {
                    this.deliveryOptions = response;
                }
            } catch {
                this.setError(resources.genericErrorMessage);
            }

            if (this.deliveryOptions.length === 0) {
                this.setError(resources.deliveryOptionsNotFound);
            } else if (this.cartDeliveryMode && this.isDeliveryModeExists(this.cartDeliveryMode)) {
                this.selectedItemId = this.cartDeliveryMode;
                this.cartDeliveryMode = undefined;
                this.onReady();

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
                this.props.data.checkout.result?.updateHasCartDeliveryMode({ newHasCartDeliveryMode: true });

                if (!this.props.data.checkout.result?.isPaymentVerificationRedirection) {
                    await this.props.data.checkout.result.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });
                }
            } else {
                this.onUpdating();
                this.selectedItemId = this.deliveryOptions.length > 0 ? this.deliveryOptions[0].Code || '' : ''; // By default, select first delivery

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
                this.props.data.checkout.result?.updateHasCartDeliveryMode({ newHasCartDeliveryMode: !!this.isSelectDefaultOptionEnabled });
            }

            this.isLoading = false;
        }
    };

    private readonly getStoredDeliveryOptions = async (): Promise<void> => {
        if (!this.props.data.checkout.result) {
            return;
        }

        const { context } = this.props;
        const cartState = this.props.data.checkout.result.checkoutCart;

        if (context) {
            this.isLoading = true;
            this.deliveryOptions = [];
            this.selectedItemId = '';
            this.clearError();

            let originalDeliveryMode: string = '';
            const shippingCartLines = this.getCartLinesForShipping();
            const hasShippingDeliveryLines = shippingCartLines && shippingCartLines.length > 0 && shippingCartLines[0].DeliveryMode;
            if (hasShippingDeliveryLines) {
                // First store the delivery mode, so that we can restore the shipping information.
                originalDeliveryMode = shippingCartLines[0].DeliveryMode || '';

                // Clear the delivery information on retail server, so GetDeliveryOptions charge calculation works correctly
                const cart = cartState.cart;
                await cartState.clearCartLinesDeliveryInformation({
                    cart,
                    channelDeliveryOptionConfiguration: this.channelDeliveryOptionConfig
                });
                if (hasShippingDeliveryLines) {
                    await this.updateCartDeliverySpecification(originalDeliveryMode);
                }
            }

            if (this.cartDeliveryMode) {
                this.selectedItemId = this.cartDeliveryMode;
                this.cartDeliveryMode = undefined;
                this.onReady();

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
                this.props.data.checkout.result?.updateHasCartDeliveryMode({ newHasCartDeliveryMode: true });

                if (!this.props.data.checkout.result.isPaymentVerificationRedirection) {
                    await this.props.data.checkout.result.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: false });
                }
            }

            this.isLoading = false;
        }
    };

    private readonly loadDeliveryOptions = async (): Promise<void> => {
        const { checkout } = this.props.data;
        const { result } = checkout || {};
        const { shippingAddress, cartLinesWithShippingAddress, isPaymentVerificationRedirection, isExpressCheckoutApplied } = result || {};
        const { features } = this.props.context.actionContext.requestContext || {};

        if (!this.canShip() || !(shippingAddress || cartLinesWithShippingAddress)) {
            return;
        }

        const shouldSubmit = this.isSelectDefaultOptionEnabled || isExpressCheckoutApplied || this.isExpressPaymentAppliedInCartPage;

        if (features?.enable_checkout_race_condition_hot_fix) {
            if (isPaymentVerificationRedirection) {
                await this.getStoredDeliveryOptions();
            } else {
                if (!isPaymentVerificationRedirection || shouldSubmit) {
                    await this.loadDeliveryOptionsBasedOnAddress(shippingAddress);
                }
            }
        } else {
            if (!isPaymentVerificationRedirection || shouldSubmit) {
                await this.loadDeliveryOptionsBasedOnAddress(shippingAddress);
            }

            if (isPaymentVerificationRedirection) {
                await this.getStoredDeliveryOptions();
            }
        }

        if (shouldSubmit) {
            await this.onSubmit();
        }
    };

    private readonly loadDeliveryOptionsBasedOnAddress = async (shippingAddress?: Address): Promise<void> => {
        if (this.isMultipleAddressShippingEnabled) {
            if (shippingAddress) {
                await this.getGroupDeliveryOptions(shippingAddress);
            } else {
                await this.getGroupDeliveryOptions();
            }
        } else if (shippingAddress) {
            await this.getDeliveryOptions(shippingAddress);
        }
    };

    private readonly onDeliveryOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const deliveryModeId = event.currentTarget.value;
        this.selectedItemId = deliveryModeId;
    };

    private readonly onCartLineDeliveryOptionChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        cartLines: CartLine[],
        cartLineId?: string
    ) => {
        const deliveryModeId = event.currentTarget.value;
        if (cartLineId !== undefined) {
            const existingCartLineSelectedDeliveryOption = this.cartLineSelectedDeliveryOptions.find(
                cartLineSelectedDeliveryOption => cartLineSelectedDeliveryOption.cartLineId === cartLineId
            );
            if (existingCartLineSelectedDeliveryOption) {
                existingCartLineSelectedDeliveryOption.selectedDeliveryOption = deliveryModeId;
            } else {
                this.cartLineSelectedDeliveryOptions.push({ cartLineId, selectedDeliveryOption: deliveryModeId });
            }
            return;
        }

        cartLines.forEach(cartLine => {
            if (!cartLine.LineId) {
                return;
            }
            const cartLineId = cartLine?.LineId.toString();
            const existingCartLineSelectedDeliveryOption = this.cartLineSelectedDeliveryOptions.find(
                cartLineSelectedDeliveryOption => cartLineSelectedDeliveryOption.cartLineId === cartLineId
            );
            if (existingCartLineSelectedDeliveryOption) {
                existingCartLineSelectedDeliveryOption.selectedDeliveryOption = deliveryModeId;
            } else {
                this.cartLineSelectedDeliveryOptions.push({ cartLineId, selectedDeliveryOption: deliveryModeId });
            }
        });
    };

    private readonly onCartLineRequestDeliveryDateChange = (event: React.ChangeEvent<HTMLInputElement>, cartLines: CartLine[]) => {
        const deliveryDate = event.currentTarget.value;
        cartLines.forEach(cartLine => {
            if (!cartLine.LineId) {
                return;
            }
            const cartLineId = cartLine?.LineId.toString();
            const existingCartLineSelectedDeliveryOption = this.cartLineSelectedDeliveryOptions.find(
                cartLineSelectedDeliveryOption => cartLineSelectedDeliveryOption.cartLineId === cartLineId
            );
            if (existingCartLineSelectedDeliveryOption) {
                existingCartLineSelectedDeliveryOption.requestDeliveryDate = deliveryDate;
            } else {
                const deliveryModeId = cartLine.DeliveryMode !== undefined ? cartLine.DeliveryMode : '';
                this.cartLineSelectedDeliveryOptions.push({
                    cartLineId,
                    selectedDeliveryOption: deliveryModeId,
                    requestDeliveryDate: deliveryDate
                });
            }
        });
    };

    private readonly onReady = () => {
        if (!(this.props.config.useDefaultDeliveryOption === useDefaultDeliveryOption.notDisplaySelectedDeliveryOption)) {
            this.props.moduleState.onReady();
        }
    };

    private readonly onUpdating = () => {
        if (!(this.props.config.useDefaultDeliveryOption === useDefaultDeliveryOption.notDisplaySelectedDeliveryOption)) {
            this.props.moduleState.onUpdating();
        }
    };

    private readonly onPending = () => {
        if (!this.isSelectDefaultOptionEnabled) {
            this.props.moduleState.onPending();
        }
    };

    private readonly getCheckoutCartLineGroupDeliveryOptions = (): React.ReactNode => {
        const { resources, config } = this.props;
        const cartConfiguration = this.props.data.cartConfiguration?.result;
        const shippingCartLines = this.getCartLinesForShipping();
        if (!ArrayExtensions.hasElements(shippingCartLines) || !ArrayExtensions.hasElements(this.groupDeliveryOptions)) {
            return null;
        }
        const imageSettings = this.getImageSettings(config.imageSettings);
        const requestDeliveryDateEnabled = config.enableRequestDeliveryDate === true && this.isMultipleAddressShippingEnabled === true;
        return (
            <div className='ms-checkout-delivery-options__cart-line-grp'>
                {this.groupDeliveryOptions.map((groupDeliveryOption: DeliveryOptionsCartLineAddress, index) => {
                    const cartLineId = groupDeliveryOption.cartLineIds !== undefined ? groupDeliveryOption.cartLineIds[0] : '';
                    const cartLine1 = shippingCartLines.find(cartLine => cartLineId === cartLine?.LineId?.toString() ?? '');
                    const cartLine = shippingCartLines.filter(
                        cartLine => groupDeliveryOption && (cartLineId === cartLine?.LineId?.toString() ?? '')
                    );
                    if (groupDeliveryOption !== undefined) {
                        const selectedDeliveryOption = this.cartLineSelectedDeliveryOptions.find(
                            cartLineSelectedDeliveryOption => cartLineSelectedDeliveryOption.cartLineId === cartLine[0].LineId
                        )?.selectedDeliveryOption;
                        return (
                            <div className='ms-checkout-delivery-options__cart-line-grp__item' key={cartLineId}>
                                <div className='ms-checkout-delivery-options__cart-line-grp__item__header'>
                                    <AddressDetailItemFunctionComponent
                                        isNewLine={true}
                                        isShowLabel={true}
                                        labelText={resources.cartLineGroupShippingAddressLabel}
                                        name='Address'
                                        value={cartLine1?.ShippingAddress?.FullAddress ?? ''}
                                    />
                                </div>
                                <div className='ms-checkout-delivery-options__cart-line-grp__item__ctn'>
                                    <div className='ms-checkout-delivery-options__cart-line-grp__item__ctn__lbl'>
                                        <span className='ms-checkout-delivery-options__cart-line-grp__item__ctn__lbl_h'>
                                            {resources.cartLineGroupDeliveryOptionsLabel}
                                        </span>
                                        <span className='ms-checkout-delivery-options__cart-line-grp__item__ctn__lbl_t'>
                                            {resources.cartLineGroupDeliveryOptionsText}
                                        </span>
                                        {requestDeliveryDateEnabled ? (
                                            <CheckoutCartLineDeliveryDate
                                                ariaLabel={resources.cartLineGroupDeliveryRequestDeliveryDateText}
                                                telemetryContent={this.telemetryContent}
                                                heading={resources.cartLineGroupDeliveryRequestDeliveryDateText}
                                                disclaimerText={resources.cartLineGroupDeliveryRequestDeliveryDateDisclaimerText}
                                                checkBoxLabel={resources.cartLineGroupDeliveryRequestDeliveryDate}
                                                cartLines={shippingCartLines}
                                                minRangeValue={cartConfiguration?.AllowedMinimumDaysForRequestDelivery}
                                                maxRangeValue={cartConfiguration?.AllowedMaximumDaysForRequestDelivery}
                                                groupDeliveryOption={groupDeliveryOption}
                                                className='ms-checkout-delivery-options__cart-line-grp__item__ctn__lbl__date'
                                                onChange={this.onCartLineRequestDeliveryDateChange}
                                            />
                                        ) : null}
                                        <CheckoutCartLineDeliveryOptionsRadio
                                            className='ms-checkout-delivery-options__cart-line-grp__item__ctn'
                                            deliveryOptions={groupDeliveryOption.deliveryOptions}
                                            priceFree={resources.priceFree}
                                            selectedItemId={selectedDeliveryOption ?? ''}
                                            typeName={this.props.typeName}
                                            context={this.props.context}
                                            cartLines={shippingCartLines}
                                            groupDeliveryOption={groupDeliveryOption}
                                            cartLineId={cartLine1?.LineId}
                                            onChange={this.onCartLineDeliveryOptionChange}
                                            ariaLabel={resources.cartLineGroupDeliveryOptionsLabel}
                                            telemetryContent={this.telemetryContent}
                                            moduleId={''}
                                            ariaSetSize={0}
                                            ariaPosInSet={0}
                                            groupName={`deliveryOptions_${cartLine1?.LineId}_${index}`} // Ensure unique name for each group
                                        />
                                    </div>
                                    {groupDeliveryOption.cartLineIds?.map(cartLineId => {
                                        const cartLine1 = shippingCartLines.find(
                                            cartLine => cartLineId === cartLine?.LineId?.toString() ?? ''
                                        );
                                        const selectedDeliveryOption1 = this.cartLineSelectedDeliveryOptions.find(
                                            cartLineSelectedDeliveryOption =>
                                                cartLineSelectedDeliveryOption.cartLineId === cartLine1?.LineId
                                        )?.selectedDeliveryOption;
                                        return (
                                            <GetLineItems
                                                key={cartLineId}
                                                className='ms-checkout-delivery-options__cart-line-grp__item__ctn-lines'
                                                context={this.props.context}
                                                cartLines={cartLine1 !== undefined ? [cartLine1] : []}
                                                products={this.props.data.products?.result}
                                                imageSettings={imageSettings}
                                                gridSettings={this.props.context.request.gridSettings}
                                                selectedItemId={selectedDeliveryOption1 ?? ''}
                                                cartLineId={cartLine1?.LineId}
                                                resources={{
                                                    configString: resources.configString,
                                                    discountStringText: resources.discountStringText,
                                                    shippingChargesText: resources.shippingChargesString,
                                                    productDimensionTypeColor: resources.productDimensionTypeColor,
                                                    productDimensionTypeSize: resources.productDimensionTypeSize,
                                                    productDimensionTypeStyle: resources.productDimensionTypeStyle,
                                                    productDimensionTypeAmount: resources.productDimensionTypeAmount,
                                                    quantityDisplayStringLabel: resources.quantityDisplayString,
                                                    priceFree: resources.priceFree,
                                                    ariaLabel: resources.cartLineGroupDeliveryOptionsLabel,
                                                    changeButtonText: resources.changeButtonText,
                                                    deliveryMethodLabel: resources.deliveryMethodLabel
                                                }}
                                                currencyCode={this.currencyCode}
                                                catalogs={this.props.data.catalogs?.result}
                                                moduleId={this.props.id}
                                                typeName={this.props.typeName}
                                                showShippingChargesForLineItems={true} // Add this line
                                                deliveryOptions={groupDeliveryOption.deliveryOptions}
                                                onChange={this.onCartLineDeliveryOptionChange}
                                                telemetryContent={this.telemetryContent}
                                                ariaSetSize={0}
                                                ariaPosInSet={0}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    } else {
                        return null;
                    }
                })}
            </div>
        );
    };

    private readonly getGroupDeliveryOptions = async (shippingAddress?: Address): Promise<void> => {
        if (!this.props.data.checkout.result) {
            return;
        }

        const { context, resources, config } = this.props;
        const cartState = this.props.data.checkout.result?.checkoutCart;

        if (context) {
            this.isLoading = true;
            this.deliveryOptions = [];
            this.selectedItemId = '';
            this.clearError();

            try {
                let originalDeliveryMode: string = '';
                const shippingCartLines = this.getCartLinesForShipping();
                const cartLinesForShipping: CartLine[] = [];
                if (ArrayExtensions.hasElements(shippingCartLines)) {
                    const getGroupByDelivery = (items: CartLine[]) => groupBy(items, (item: CartLine) => item.ShippingAddress?.RecordId);
                    cartLinesForShipping.push(getGroupByDelivery(shippingCartLines));
                    console.log('running delivery option for all lines');
                }
                const hasShippingDeliveryLines = shippingCartLines && shippingCartLines.length > 0 && shippingCartLines[0].DeliveryMode;
                const cart = cartState.cart;
                if (hasShippingDeliveryLines) {
                    // First store the delivery mode, so that we can restore the shipping information.
                    originalDeliveryMode = shippingCartLines[0].DeliveryMode || '';

                    // Clear the delivery information on retail server, so GetDeliveryOptions charge calculation works correctly
                    await cartState.clearCartLinesDeliveryInformation({
                        cart,
                        channelDeliveryOptionConfiguration: this.channelDeliveryOptionConfig
                    });
                }

                // TODO: Check if we can move this getDeliveryOptions to checkout state
                let deliveryOptionFilter: FilterDeliveryModeOption;
                switch (config.filterDeliveryModeOption) {
                    case filterDeliveryModeOption.none:
                        deliveryOptionFilter = FilterDeliveryModeOption.None;
                        break;
                    case filterDeliveryModeOption.filterOutNonCarrierDeliveryMode:
                        deliveryOptionFilter = FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode;
                        break;
                    default:
                        deliveryOptionFilter = FilterDeliveryModeOption.FilterOutNonCarrierDeliveryMode;
                }
                let response: DeliveryOptionsCartLineAddress[] = [];
                if (shippingAddress !== undefined && shippingAddress.RecordId !== undefined) {
                    const checkoutState = this.props.data.checkout.result;
                    const cartLineIds = shippingCartLines.map(cartLine => (cartLine.LineId !== undefined ? cartLine.LineId : ''));
                    await getDeliveryOptionsAsync(
                        {
                            callerContext: context.actionContext,
                            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context.actionContext)
                        },
                        cart.Id,
                        shippingAddress,
                        [],
                        deliveryOptionFilter,
                        true
                    ).then(async result => {
                        if (result && result.length > 0) {
                            response.push({ deliveryOptions: result, hasError: false, error: undefined, cartLineIds: cartLineIds });
                            shippingCartLines?.forEach(cartLine => {
                                cartLine.ShippingAddress = shippingAddress;
                            });
                            await checkoutState.checkoutCart.updateCartLines({ updatedCartLinesObject: shippingCartLines });
                            // console.log(updateLineDeliverySpecifications());
                        } else {
                            response.push({
                                deliveryOptions: [],
                                hasError: true,
                                error: 'No delivery options available',
                                cartLineIds: cartLineIds
                            });
                        }
                    });
                } else {
                    const inputData = new GetDeliveryOptionsForCartLineAddressInput(deliveryOptionFilter, shippingCartLines);
                    response = await getDeliveryOptionsForCartLineAddress(inputData, context.actionContext);
                }
                if (response && response.length > 0) {
                    this.groupDeliveryOptions = response;
                    console.log('groupDeliveryOptions');
                }
                if (this.groupDeliveryOptions.length === 0) {
                    this.setError(resources.deliveryOptionsNotFound);
                } else if (this.cartLinesDeliveryOptions.length !== 0) {
                    this.cartLineSelectedDeliveryOptions = this.cartLinesDeliveryOptions;
                    this.cartLinesDeliveryOptions = [];
                    this.onReady();
                } else {
                    this.onUpdating();
                    this.groupDeliveryOptions.forEach(group => {
                        const selectedDeliveryOption =
                            group.deliveryOptions.length > 0 && group.deliveryOptions[0].Code !== undefined
                                ? group.deliveryOptions[0].Code
                                : '';
                        group.cartLineIds?.forEach(cartLineId => {
                            const existingCartLineSelectedDeliveryOption = this.cartLineSelectedDeliveryOptions.find(
                                cartLineDeliveryOption => cartLineDeliveryOption.cartLineId === cartLineId
                            );
                            if (existingCartLineSelectedDeliveryOption) {
                                existingCartLineSelectedDeliveryOption.selectedDeliveryOption = selectedDeliveryOption;
                            } else {
                                this.cartLineSelectedDeliveryOptions.push({
                                    cartLineId,
                                    selectedDeliveryOption
                                });
                            }
                        });
                    });
                }

                if (hasShippingDeliveryLines) {
                    await this.updateCartDeliverySpecification(originalDeliveryMode);
                }
            } catch {
                this.setError(resources.genericErrorMessage);
            }
        }
        this.isLoading = false;
    };

    /**
     * Gets the image settings.
     * @param imageSettings - Image settings.
     * @returns IImageSettings.
     */
    private readonly getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            imageSettings || {
                viewports: {
                    xs: { q: 'w=80&h=94&m=6', w: 0, h: 0 },
                    sm: { q: 'w=148&h=174&m=6', w: 0, h: 0 },
                    lg: { q: 'w=148&h=174&m=6', w: 0, h: 0 }
                },
                lazyload: true
            }
        );
    };

    private readonly getMultipleDeliveryOptionSelected = (): ICheckoutShippingCartLineInfo[] => {
        const products = this.props.data.products?.result;
        const cartLineImages: ICheckoutShippingCartLineInfo[] = [];

        this.cartLineSelectedDeliveryOptions.map(cartLineDeliveryOption => {
            const cartLine = this.props.data.checkout.result?.checkoutCart.cart.CartLines?.find(
                (cartLine: CartLine) => cartLine.LineId === cartLineDeliveryOption.cartLineId
            );
            if (cartLine) {
                const product = products?.find((product: SimpleProduct) => product.RecordId === cartLine.ProductId);
                const imageProps: IImageProps = {
                    requestContext: this.props.context.actionContext.requestContext,
                    className: 'ms-checkout-shipping-address__group-images-lines-line-image',
                    altText: product?.Name,
                    src: product?.PrimaryImageUrl ?? '',
                    fallBackSrc: getFallbackImageUrl(
                        product?.ItemId,
                        this.props.context.actionContext.requestContext.apiSettings,
                        this.props.context.request.app?.config?.OmniChannelMedia
                    ),
                    gridSettings: this.props.context.request.gridSettings!,
                    imageSettings: this.props.config.imageSettings ?? defaultImageSettings,
                    loadFailureBehavior: 'empty'
                };
                let deliveryModeDesc = this.deliveryOptions.find(
                    deliveryOption => deliveryOption.Code === cartLineDeliveryOption.selectedDeliveryOption
                )?.Description;
                if (StringExtensions.isNullOrEmpty(deliveryModeDesc)) {
                    const group = this.groupDeliveryOptions.find(group => group.cartLineIds?.includes(cartLineDeliveryOption.cartLineId));
                    const deliveryOption = group?.deliveryOptions.find(
                        option => option.Code === cartLineDeliveryOption.selectedDeliveryOption
                    );
                    deliveryModeDesc = deliveryOption?.Description;
                }
                const cartLineDisplay = {
                    lineId: cartLine.LineId,
                    imageProps: imageProps,
                    deliveryMethodDesc: deliveryModeDesc,
                    requestDeliveryDate: cartLineDeliveryOption.requestDeliveryDate
                };
                cartLineImages.push(cartLineDisplay);
            }
        });

        return cartLineImages;
    };
}

export default CheckoutDeliveryOptions;
